// src/components/ErrorPage/ErrorModal.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import "./ErrorModal.scss";
import { useAuth0 } from "@auth0/auth0-react";
import { AlertCircle, Home, RefreshCw, ChevronLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AnimatePresence, motion } from "framer-motion";

interface ErrorState {
  code: string;
  title: string;
  message: string;
  correlationId: string;
  timestamp?: string;
}

interface ErrorModalProps {
  errorState: ErrorState;
  onClose: () => void;
}

const ErrorModal: React.FC<ErrorModalProps> = ({ errorState, onClose }) => {
  // const navigate = useNavigate();
  const { loginWithRedirect, logout } = useAuth0();

  const handleGoHome = () => {
    onClose(); // Close modal first
    window.location.href = "/";
  };

  const handleLogin = async () => {
    onClose();

    await logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    });

    loginWithRedirect();
  };

  // Determine icon based on error code
  const getErrorIcon = () => {
    const code = errorState.code;

    if (code === "404") {
      return (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
        />
      );
    } else if (code === "401" || code === "403") {
      return (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
        />
      );
    } else if (code === "NETWORK") {
      return (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414"
        />
      );
    } else {
      return (
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
        />
      );
    }
  };
  return (
    <AnimatePresence>
      <div className="error-modal-container min-h-screen flex items-center justify-center fixed inset-0 z-[9999]">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        />

        {/* Modal Content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{
            duration: 0.4,
            ease: [0.16, 1, 0.3, 1], // Spring-like ease out
            scale: { type: "spring", damping: 25, stiffness: 300 },
          }}
          className="relative w-full max-w-2xl px-4"
        >
          <Card className="w-full overflow-hidden border-0 shadow-2xl error-modal-content">
            {/* Error Card */}

            <CardHeader className="pb-2 text-center">
              <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 rounded-full bg-red-50">
                <AlertCircle className="w-8 h-8 text-red-600" />
              </div>
              <CardTitle className="text-2xl font-bold text-gray-900">
                Oops! Something went wrong
              </CardTitle>
              <CardDescription className="mt-2 text-base text-gray-600">
                We're sorry for the inconvenience. Don't worry, your data is
                safe.
              </CardDescription>
            </CardHeader>

            <CardContent className="pt-4">
              {errorState.message && (
                <Alert
                  variant="destructive"
                  className="border-red-200 bg-red-50"
                >
                  <AlertCircle className="w-4 h-4" />
                  <AlertTitle className="text-destructive">
                    {errorState.title || "Error message"}
                  </AlertTitle>
                  <AlertDescription className="mt-0 text-sm font-medium leading-relaxed">
                    {errorState.message || "Something unexpected happened."}
                  </AlertDescription>
                </Alert>
              )}

              <div className="p-4 mt-6 border border-blue-100 rounded-lg bg-blue-50">
                <h4 className="mb-2 text-sm font-semibold text-blue-900">
                  What can you do?
                </h4>
                <ul className="text-sm text-blue-800 space-y-1.5 list-disc list-inside">
                  <li>Try refreshing the page</li>
                  <li>Go back to the previous page</li>
                  <li>Return to the home page</li>
                  <li>If the problem persists, contact support</li>
                </ul>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col justify-center gap-3 pt-2 pb-6 sm:flex-row">
              <Button
                onClick={onClose}
                variant="outline"
                className="w-full sm:w-auto min-w-[120px]"
              >
                <ChevronLeft className="w-4 h-4 mr-2" />
                Go Back
              </Button>
              {/* <Button
                      onClick={handleRefresh}
                      variant="outline"
                      className="w-full sm:w-auto"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh Page
                    </Button> */}
              {errorState.code === "401" ? (
                <Button
                  onClick={handleLogin}
                  className="w-full sm:w-auto min-w-[120px]"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Login
                </Button>
              ) : (
                <Button
                  onClick={handleGoHome}
                  className="w-full sm:w-auto min-w-[120px]"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Go Home
                </Button>
              )}
            </CardFooter>
          </Card>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ErrorModal;
